// The insurance Renewal Plan stage catalog — ONE source of truth for
// the renewal workflow (AC-name parity, locked by Grant 2026-07-11;
// replaces the 8-stage list from the Joy feedback program PR 6 so the
// team sees ActiveCampaign's familiar renewal column names at cutover).
//
// CONSUMER: shared (Workers runtime + web via @shared + tests).
//
// These stages describe where a renewal CYCLE is in Joy's quote-review
// workflow — they live on rhythm_instances.renewal_stage and apply
// ONLY to insurance Renewal Plan cycles (template slug 'renewal_plan').
// Other plan types (Birthday, Annual Review, Rate Watch) never carry a
// stage; the column stays NULL for them by validation, not convention.
//
// DECISION (Grant 2026-07-11): the catalog is AC's renewal stage names
// minus "Update Active Campaign" (an instruction, not a stage), PLUS an
// explicit "Complete" terminal — AC ended cycles by winning the deal;
// the OS needs cycles to close explicitly so roll-forward works.
//
// NULL semantics: a renewal cycle with NULL renewal_stage displays as
// "90 Days Prior To Expiration" (the entry stage). Migration 0131
// backfilled active cycles at the legacy entry key; rows still carrying
// LEGACY keys (below) coalesce to the entry stage in the UI until the
// gated remap CLI (migration/activecampaign/src/stage-parity-remap.ts)
// rewrites them on Grant's go. Pure + dependency-free, like every
// shared catalog.

export const RENEWAL_STAGES = [
  { key: "90_days_prior_to_expiration", label: "90 Days Prior To Expiration" },
  { key: "prep", label: "Prep" },
  { key: "intake_form_review", label: "Intake Form Review" },
  { key: "quoting", label: "Quoting" },
  { key: "quote_review", label: "Quote Review" },
  { key: "quotes_delivered", label: "Quotes Delivered" },
  { key: "stay_recommended", label: "Stay Recommended" },
  { key: "change_recommended", label: "Change Recommended" },
  { key: "holding", label: "Holding" },
  { key: "complete", label: "Complete" },
] as const;

export type RenewalStage = (typeof RENEWAL_STAGES)[number]["key"];

export const RENEWAL_STAGE_KEYS: readonly RenewalStage[] = RENEWAL_STAGES.map(
  (s) => s.key,
);

/** The entry stage — where NULL / unknown values coalesce. */
export const RENEWAL_ENTRY_STAGE: RenewalStage = "90_days_prior_to_expiration";

// Legacy (pre-2026-07-11) catalog key → AC-parity key. The single
// source of truth for the gated data-remap CLI AND the reference table
// Grant reviews before the apply. `stay_recommended` / `complete`
// carried over unchanged; they're listed so the map is total over the
// old catalog (the CLI skips no-op rows).
//
// Semantic notes on the two non-obvious rows (per Grant's table,
// 2026-07-11):
//   * quoted → quote_review        (old "Quoted" meant quotes were in
//                                   hand being reviewed, not yet out)
//   * decision_received → quotes_delivered  (closest AC column for
//                                   "client responded to the quotes";
//                                   Stay/Change Recommended are Joy's
//                                   OWN recommendation, which the old
//                                   catalog captured separately)
export const LEGACY_RENEWAL_STAGE_REMAP: Readonly<
  Record<string, RenewalStage>
> = {
  renewal_approaching: "90_days_prior_to_expiration",
  intake_sent: "prep",
  intake_received: "intake_form_review",
  quoted: "quote_review",
  stay_recommended: "stay_recommended",
  decision_received: "quotes_delivered",
  finalizing: "holding",
  complete: "complete",
};

const STAGE_LABELS = new Map<string, string>(
  RENEWAL_STAGES.map((s) => [s.key, s.label]),
);

export function isRenewalStage(value: unknown): value is RenewalStage {
  return typeof value === "string" && STAGE_LABELS.has(value);
}

/** Display label; NULL/unknown coalesces to the entry stage's label. */
export function renewalStageLabel(stage: string | null): string {
  if (stage && STAGE_LABELS.has(stage)) return STAGE_LABELS.get(stage)!;
  return STAGE_LABELS.get(RENEWAL_ENTRY_STAGE)!;
}

/** Board-column key with NULL coalesced to the entry stage. */
export function effectiveRenewalStage(stage: string | null): RenewalStage {
  return isRenewalStage(stage) ? stage : RENEWAL_ENTRY_STAGE;
}

/** 0-based position in the workflow (for ordering + progress). */
export function renewalStageIndex(stage: string | null): number {
  const key = effectiveRenewalStage(stage);
  return RENEWAL_STAGES.findIndex((s) => s.key === key);
}
